import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getFeeDueInfo } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");
const feeHeadApi = createLookupApi("fee-heads");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Direct port of digicampusbd's real "Fee Due Info" (`fee-report-student-due-details`,
 * `FeeReportService.viewSectionTotalPaidDetailsByFeeHeadsAndMonths`) — found missing while
 * sweeping Fee Management (see MIGRATION.md): this migration had wrongly wired that real,
 * staff-facing menu URL to a much simpler due-list page instead. For one section+year+group
 * +category (+ optional roll range), each student's payable/paid/due broken down by BOTH the
 * caller's chosen fee heads AND chosen months — the one due report in the real app that is
 * individual-assign/discard-aware (every other due report in this app documents that as a
 * simplification it does NOT fold in; this one does, since that's the real behavior here).
 */
export default function FeeDueInfoPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadIds, setFeeHeadIds] = useState([]);
  const [months, setMonths] = useState([]);
  const [startRoll, setStartRoll] = useState("");
  const [endRoll, setEndRoll] = useState("");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then((list) => {
      setGroups(list);
      if (list.length > 0) setGroupId(String(list[0].id));
    });
    categoryApi.list().then((list) => {
      setCategories(list);
      if (list.length > 0) setCategoryId(String(list[0].id));
    });
    feeHeadApi.list().then(setFeeHeads);
  }, []);

  const toggleFeeHead = (id) => setFeeHeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleMonth = (m) => setMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const handleSearch = async () => {
    setError("");
    setResult(null);
    if (!academicYear || !classConfigId || !groupId || !categoryId) {
      setError("Please select academic year, section, group, and category.");
      return;
    }
    if (feeHeadIds.length === 0 || months.length === 0) {
      setError("Select at least one fee head and one month.");
      return;
    }
    setLoading(true);
    try {
      const data = await getFeeDueInfo(academicYear, classConfigId, groupId, categoryId, feeHeadIds, months, startRoll, endRoll);
      setResult(data);
      if ((data.students || []).length === 0) {
        setError("No students found for this selection.");
      }
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load Fee Due Info.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Due Info</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="form-row">
        <span className="admit-card-label">Fee Heads:</span>{" "}
        {feeHeads.map((h) => (
          <label key={h.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={feeHeadIds.includes(h.id)} onChange={() => toggleFeeHead(h.id)} />
            {h.name}
          </label>
        ))}
      </div>

      <div className="form-row">
        <span className="admit-card-label">Months:</span>{" "}
        {MONTHS.map((m) => (
          <label key={m} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 10 }}>
            <input type="checkbox" checked={months.includes(m)} onChange={() => toggleMonth(m)} />
            {m}
          </label>
        ))}
      </div>

      <div className="attendance-filters">
        <label>
          Start Roll (optional)
          <input type="number" value={startRoll} onChange={(e) => setStartRoll(e.target.value)} />
        </label>
        <label>
          End Roll (optional)
          <input type="number" value={endRoll} onChange={(e) => setEndRoll(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      {result && result.students.length > 0 && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Name</th>
                <th>Roll</th>
                <th>Group</th>
                {months
                  .filter((m) => MONTHS.includes(m))
                  .sort((a, b) => MONTHS.indexOf(a) - MONTHS.indexOf(b))
                  .map((m) => (
                    <th key={m}>{m}</th>
                  ))}
                <th>Total Payable</th>
                <th>Total Paid</th>
                <th>Total Due</th>
              </tr>
            </thead>
            <tbody>
              {result.students.map((s) => (
                <tr key={s.identificationId}>
                  <td>{s.customStudentId}</td>
                  <td>{s.studentName}</td>
                  <td>{s.studentRoll}</td>
                  <td>{s.groupName || "-"}</td>
                  {months
                    .filter((m) => MONTHS.includes(m))
                    .sort((a, b) => MONTHS.indexOf(a) - MONTHS.indexOf(b))
                    .map((m) => {
                      const lines = (s.monthlyDetails && s.monthlyDetails[m]) || [];
                      return (
                        <td key={m}>
                          {lines.length === 0
                            ? "-"
                            : lines.map((l, idx) => (
                                <div key={idx} style={{ fontSize: "0.85rem" }}>
                                  {l.feeHeadName}: {Math.round(l.dueAmount)}
                                </div>
                              ))}
                        </td>
                      );
                    })}
                  <td>{Math.round(s.totalFeeAmount)}</td>
                  <td>{Math.round(s.totalFeePaidAmount)}</td>
                  <td>
                    <strong>{Math.round(s.totalFeeDueAmount)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4 + months.length} style={{ fontWeight: 800, textAlign: "right" }}>
                  Grand Total
                </td>
                <td>
                  <strong>{Math.round(result.students.reduce((sum, s) => sum + s.totalFeeAmount, 0))}</strong>
                </td>
                <td>
                  <strong>{Math.round(result.students.reduce((sum, s) => sum + s.totalFeePaidAmount, 0))}</strong>
                </td>
                <td>
                  <strong>{Math.round(result.students.reduce((sum, s) => sum + s.totalFeeDueAmount, 0))}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </MainLayout>
  );
}
